import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { isEmpty, pathOr } from 'ramda';
import { Icon } from '@modulor/react';
import Local from '../Local/Local';
import { useQueryGames } from '../../hooks/API/useQueryGames';
import { useInfinityQuerySearchGames } from '../../hooks/API/useQuerySearchGames';
import { useQueryRecentSearchedGames } from '../../hooks/API/useQueryStorageGames';
import withAnalytics from '../../hocs/withAnalytics';
import { getSearchRoute } from '../../utils/routes';
import { getDataTestAttrs } from '../../utils/testing-locators';
import { MIN_COUNT_CHARACTERS_FOR_SEARCH } from '../../constants/search';
import { EVENT_ACTION } from '../../constants/ga';
import {
  QUICK_SEARCH,
  QUICK_SEARCH_TITLE,
  QUICK_SEARCH_GAME_LIST,
  QUICK_SEARCH_RESULT_LINK,
} from '../../constants/dataTestIds';
import QuickSearchItem from './QuickSearchItem';
import styles from './QuickSearch.module.css';
import {
  ANALYTICS_SCHEMA_URLS,
  ANALYTICS_SCREEN_AREAS,
  ANALYTICS_SCREEN_AREA_DETAILS,
} from '../../constants/analytics';
import { useAnalyticsScreenName } from '../../hooks/useAnalyticsScreenName';

export const LinkWithAnalytics = withAnalytics(Link);

export const MAX_COUNT_GAMES = 6;

const QuickSearch = ({ query, className }) => {
  const isSearching =
    !!query && query.length >= MIN_COUNT_CHARACTERS_FOR_SEARCH;

  const querySearchResult = useInfinityQuerySearchGames(
    { search: query },
    {
      enabled: isSearching,
    }
  );
  const queryRecentSearchedGames = useQueryRecentSearchedGames();
  const queryGames = useQueryGames({ query: { _limit: MAX_COUNT_GAMES } });

  const recentSearchedGames = pathOr([], ['data'], queryRecentSearchedGames);
  const popularGames = pathOr([], ['data', 'items'], queryGames);
  const searchResult = pathOr([], ['data'], querySearchResult);

  const hasRecents =
    queryRecentSearchedGames.isSuccess && !isEmpty(recentSearchedGames);

  const fallbackCollection = hasRecents ? recentSearchedGames : popularGames;

  const collection = (!isSearching ? fallbackCollection : searchResult).slice(
    0,
    MAX_COUNT_GAMES
  );

  const isTooShortQuery =
    !!query && query.length < MIN_COUNT_CHARACTERS_FOR_SEARCH;
  const isEmptySearchResults =
    isSearching && !querySearchResult?.isLoading && isEmpty(collection);

  const reservedEventAction = isEmpty(recentSearchedGames)
    ? EVENT_ACTION.QUICK_SEARCH_CATEGORY_GAME
    : EVENT_ACTION.QUICK_SEARCH_PREV_GAME;
  const eventAction = !isSearching
    ? reservedEventAction
    : EVENT_ACTION.QUICK_SEARCH_FOUND_GAME;

  const screenName = useAnalyticsScreenName();

  return (
    <div
      className={classNames(styles.component, className)}
      {...getDataTestAttrs(QUICK_SEARCH)}
    >
      {!isSearching && (
        <span
          className={styles.title}
          {...getDataTestAttrs(QUICK_SEARCH_TITLE)}
        >
          {isTooShortQuery && (
            <>
              <Local values={{ value: MIN_COUNT_CHARACTERS_FOR_SEARCH }}>
                The minimum number of characters to start the search is
              </Local>
              <br />
            </>
          )}
          {isEmpty(recentSearchedGames) ? (
            <Local>Most popular games</Local>
          ) : (
            <Local>Your latest inquiries</Local>
          )}
        </span>
      )}

      {isEmptySearchResults ? (
        <span className={styles.empty}>
          <Local values={{ value: query }}>No results for</Local>
        </span>
      ) : (
        <ul {...getDataTestAttrs(QUICK_SEARCH_GAME_LIST)}>
          {collection.map((item, index) => (
            <li key={item.id} className={styles.item}>
              <QuickSearchItem
                id={item.id}
                translationKey={item.translationKey}
                routeState={{ gameFromSearch: isSearching }}
                dataLayerEventData={{
                  eventAction: eventAction,
                  game_id: item.id,
                  provider_id: item.provider,
                  section_index: index,
                }}
                analyticsData={{
                  schema: ANALYTICS_SCHEMA_URLS.PLAY_GAME_CLICK,
                  screen_name: screenName,
                  screen_area: ANALYTICS_SCREEN_AREAS.FAST_SEARCH_INPUT,
                  screen_area_detail: isSearching
                    ? ANALYTICS_SCREEN_AREA_DETAILS.RELEVANCE_GAME
                    : isEmpty(recentSearchedGames)
                    ? ANALYTICS_SCREEN_AREA_DETAILS.TOP_GAMES
                    : ANALYTICS_SCREEN_AREA_DETAILS.LATEST_INQUIRIES,
                  position_index: index,
                  section_index: 0,
                  game_id: item.id,
                  provider_id: item.provider,
                }}
              />
            </li>
          ))}
        </ul>
      )}
      {isSearching && !isEmptySearchResults && (
        <LinkWithAnalytics
          to={`${getSearchRoute()}?q=${query}`}
          dataLayerEventData={{
            eventAction: EVENT_ACTION.QUICK_SEARCH_ALL_GAMES,
            section_index: collection.length,
          }}
          className={styles['show-more']}
          analyticsData={{
            schema: ANALYTICS_SCHEMA_URLS.MORE_GAME_CLICK,
            screen_name: screenName,
            screen_area: ANALYTICS_SCREEN_AREAS.FAST_SEARCH_INPUT,
            screen_area_detail:
              ANALYTICS_SCREEN_AREA_DETAILS.GO_TO_SEARCH_RESULT,
            section_index: 0,
          }}
          {...getDataTestAttrs(QUICK_SEARCH_RESULT_LINK)}
        >
          <Icon name="search" className={styles['search-icon']} />
          <Local values={{ query }}>Go to</Local>
        </LinkWithAnalytics>
      )}
    </div>
  );
};

QuickSearch.propTypes = {
  query: PropTypes.string,
};

QuickSearch.defaultProps = {
  query: '',
};

export default QuickSearch;
